import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Boxes, Package, Workflow, Layers, Warehouse, ClipboardCheck, BookUser, LogOut, User, ShoppingCart, FileText, Settings as SettingsIcon, Truck, Ship, ExternalLink, Factory, LayoutGrid, Wrench, Menu, Receipt } from "lucide-react";
import { TestEnvBanner } from "@/components/TestEnvBanner";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/contacts", label: "Contacts", icon: BookUser, testid: "nav-contacts" },
  { to: "/sales-orders", label: "Sales Orders", icon: ShoppingCart, testid: "nav-sales-orders" },
  { to: "/purchase-orders", label: "Inventory Purchasing", icon: FileText, testid: "nav-purchase-orders" },
  { to: "/operational-expenses", label: "Operational Expenses", icon: Receipt, testid: "nav-operational-expenses" },
  { to: "/production-planning", label: "Manufacturing Orders", icon: ClipboardCheck, testid: "nav-production-planning" },
  { to: "/inventory", label: "Component Inventory", icon: Warehouse, testid: "nav-inventory" },
  { to: "/finished-goods", label: "Finished Goods", icon: Boxes, testid: "nav-finished-goods" },
  { to: "/copacker-inventory", label: "Co-Packer Inventory", icon: Factory, testid: "nav-copacker-inventory" },
  { to: "/", label: "Components", icon: Package, testid: "nav-components", end: true },
  { to: "/product-boms", label: "Product BOMs", icon: Layers, testid: "nav-boms" },
  { to: "/delivery-orders", label: "Shipments", icon: Truck, testid: "nav-delivery-orders" },
  { to: "/material-flows", label: "Material Flows", icon: Workflow, testid: "nav-material-flows" },
  { to: "/machines", label: "Machine Maintenance", icon: Wrench, testid: "nav-machines" },
  { href: "https://allwaysusa.com", label: "Ocean Freight", icon: Ship, testid: "nav-ocean-freight" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const toggle = () => setCollapsed((c) => { localStorage.setItem("sidebar_collapsed", c ? "0" : "1"); return !c; });

  return (
    <div className="min-h-screen bg-white">
      <TestEnvBanner />
      <div className="flex">
      {collapsed && (
        <button
          onClick={toggle}
          title="Show sidebar"
          data-testid="sidebar-open-btn"
          className="fixed left-0 top-20 z-40 rounded-r-sm border border-l-0 border-slate-200 bg-white py-2.5 pl-1 pr-1.5 text-slate-500 shadow-sm transition-colors hover:text-[#002FA7]"
        >
          <Menu className="h-4 w-4" />
        </button>
      )}
      <aside className={`sticky top-0 h-screen shrink-0 flex-col border-r border-slate-200 bg-white ${collapsed ? "hidden" : "flex w-16 md:w-64"}`}>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-4 md:px-6">
          <Boxes className="h-6 w-6 shrink-0 text-[#002FA7]" strokeWidth={1.5} />
          <span className="hidden font-heading text-lg font-bold tracking-tight text-slate-900 md:inline">
            Replenish Supply
          </span>
          <button
            onClick={toggle}
            title="Hide sidebar"
            data-testid="sidebar-collapse-btn"
            className="ml-auto hidden shrink-0 text-slate-400 transition-colors hover:text-[#002FA7] md:block"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 p-2 md:p-3">
          {NAV.map(({ to, href, label, icon: Icon, testid, end, group }) =>
            group ? (
              <p key={group} className="hidden px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 first:pt-1 md:block">{group}</p>
            ) : href ? (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testid}
                className="flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                title={label}
              >
                <Icon className="h-4.5 w-4.5 shrink-0" strokeWidth={1.75} />
                <span className="hidden items-center gap-1.5 md:inline-flex">
                  {label}
                  <ExternalLink className="h-3 w-3 text-slate-400" />
                </span>
              </a>
            ) : (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-[#002FA7]/10 font-semibold text-[#002FA7]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
              title={label}
            >
              <Icon className="h-4.5 w-4.5 shrink-0" strokeWidth={1.75} />
              <span className="hidden md:inline">{label}</span>
            </NavLink>
            )
          )}
        </nav>

        <div className="border-t border-slate-200 p-2 md:p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 rounded-sm px-3 text-slate-600"
                data-testid="user-menu"
              >
                <User className="h-4 w-4 shrink-0" />
                <span className="hidden max-w-[150px] truncate text-sm md:inline">
                  {user?.email}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-sm">
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-link">
                <SettingsIcon className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
      </div>
    </div>
  );
}
